import {PatientExtended, PatientLastInfo} from '@/features/patients/types'
import {ResumeProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {getNextWorkingDay} from '@/features/projects/utils'
import {ReferringSpecialist} from '@/features/referring-specialists/types'
import {useTranslation} from '@/translations/i18n'
import {raise} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import {ReactNode} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

export const ResumeProjectFormProvider = ({
    children,
    project,
    patient,
    treatmentRequestId,
    specialist,
    holidays
}: {
    children: ReactNode
    patient: PatientExtended
    project: NonNullable<PatientLastInfo['project']>
    treatmentRequestId: string
    specialist?: ReferringSpecialist
    holidays: {date: string}[]
}) => {
    const {t} = useTranslation()
    const startDate = getNextWorkingDay(dayjs(project.expirationDate).toDate(), holidays)
    const expirationDate =
        startDate && project.durationDays ? dayjs(startDate).add(project.durationDays, 'day').toDate() : undefined

    const form = useForm<ResumeProjectFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ResumeProjectFormValidationSchema),
        defaultValues: {
            id: project.id,
            treatmentRequestId: treatmentRequestId ?? raise(t('projects:renew_missing_treatment_request')),
            patient: {
                id: patient?.id,
                fullName: patient?.fullName,
                serviceType: patient?.serviceType
            },
            referringProject: project,
            siarProject: 'same_as_previous',
            projectType: project.type || undefined,
            // extended and maintenance sectors are deprecated sectors, so we convert them to mixed
            sector: project.sector == 'extended' || project.sector == 'maintenance' ? 'mixed' : project.sector,
            assistiveRegime: project.assistiveRegime || undefined,
            startDate,
            expirationDate,
            durationDays: project.durationDays ? project.durationDays : undefined,
            sender: project.sender || undefined,
            specialist: specialist || undefined
        }
    })

    return <FormProvider {...form}>{children}</FormProvider>
}
